import type { MessageDescriptor } from 'react-intl';
import {
  REELS_PLAN_MAX_DAYS,
  REELS_PLAN_MIN_DAYS,
  REELS_PLAN_POSTS_PER_DAY,
  reelsPlanCanRepeat,
  reelsPlanDateOf,
  reelsPlanOffsets,
} from 'fym-shared/src/music24/reelsPlan';
import { limitShortfall, type LimitShortfall } from 'fym-shared/src/music24/limitUpsell';
import type {
  ReelsPlanCadence,
  ReelsPlanPostsPerDay,
  ReelsPlanPreview,
  ReelsPlanRefusal,
  ReelsPlanSettings,
  ReleaseReelsPlan,
} from '../../../../../../redux/api/fymAPI.types';
import { platformTakesMedia } from '../../../platformMeta';
import type { SocialPlatform } from '../../../types';
import { addDays } from '../planMode';
import type { PlanStepView } from '../rollout/rolloutModel';
import { reelsPlanMessages } from './reelsPlanMessages';

/**
 * The pure rules of the AI reels plan panel (design rollout-plan f1-f6, spec 2026-10-12). The day
 * rule and the limits are in fym-shared (`music24/reelsPlan.ts`); the server is the judge of every
 * refusal (the dry run).
 */

export const REELS_PLAN_DEFAULT_DAYS = 14;
export const REELS_PLAN_DEFAULT_CADENCE: ReelsPlanCadence = 'every2Days';
export const REELS_PLAN_DEFAULT_POSTS_PER_DAY: ReelsPlanPostsPerDay = 1;
/** A change of a control waits this long before the dry run (frame f1). */
export const REELS_PLAN_PREVIEW_DEBOUNCE_MS = 300;
/** The run sheet shows this many steps and folds the others (frame f3). */
export const REELS_PLAN_VISIBLE_STEPS = 5;
/** The fold line names at most this many days. */
export const REELS_PLAN_FOLD_NAMES = 4;

/** What the panel shows: the setup, the run sheet, or the closed line above a new setup. */
export type ReelsPlanPanelState = 'setup' | 'running' | 'closed';

/**
 * The state of the panel from `reelsPlan` of the plan answer. `today` is `YYYY-MM-DD` in the time
 * zone of the artist: an answer that was read before midnight does not keep a past plan open.
 */
export function reelsPlanPanelState(plan: ReleaseReelsPlan | null | undefined, today: string): ReelsPlanPanelState {
  if (plan === null || plan === undefined) {
    return 'setup';
  }
  return plan.active && today.slice(0, 10) <= plan.lastDay ? 'running' : 'closed';
}

/** The defaults of the setup (frame f1): 14 days, every 2 days, 1 post a day, tomorrow. */
export function defaultReelsPlanSettings(today: string): ReelsPlanSettings {
  return {
    startDay: addDays(today, 1),
    days: REELS_PLAN_DEFAULT_DAYS,
    cadence: REELS_PLAN_DEFAULT_CADENCE,
    postsPerDay: REELS_PLAN_DEFAULT_POSTS_PER_DAY,
  };
}

export interface ReelsPlanPostsOption {
  value: ReelsPlanPostsPerDay;
  /** The plan of the workspace does not allow this many posts a day: a click opens the plan offer. */
  locked: boolean;
}

/**
 * The "Posts a day" options of the setup (owner decision 2026-10-19): 1, 2 and 3. An option above
 * the most posts a day of the plan (`reelsPlanMaxPostsPerDay`; 1 on Free and the trial) is locked.
 */
export function reelsPlanPostsOptions(maxPostsPerDay: number): ReelsPlanPostsOption[] {
  return REELS_PLAN_POSTS_PER_DAY.map((value) => ({ value, locked: value > maxPostsPerDay }));
}

/**
 * The most posts a day of the workspace. The dry run of the server wins; before its answer the
 * capabilities of the licence decide. A missing or broken value counts as 1.
 */
export function reelsPlanMaxPostsPerDay(
  preview: ReelsPlanPreview | null | undefined,
  capabilityMax: number | undefined
): number {
  const fromPreview = preview?.maxPostsPerDay;
  if (typeof fromPreview === 'number' && fromPreview >= 1) {
    return fromPreview;
  }
  return typeof capabilityMax === 'number' && capabilityMax >= 1 ? capabilityMax : 1;
}

/**
 * "21 posts · 12 left this month in your plan": the posts of the campaign and the scheduled-post
 * units left this month. Null for a plan with no limit, or while there is no answer. It never stops
 * the campaign: the drafts cost no unit, and the launch says which posts wait for a unit.
 */
export function reelsPlanScheduledLine(
  preview: ReelsPlanPreview | null | undefined
): { posts: number; left: number } | null {
  if (preview === null || preview === undefined) {
    return null;
  }
  const { scheduledPostsLimit, scheduledPostsLeft, postCount } = preview;
  if (typeof scheduledPostsLimit !== 'number' || typeof postCount !== 'number') {
    return null;
  }
  return { posts: postCount, left: Math.max(0, typeof scheduledPostsLeft === 'number' ? scheduledPostsLeft : 0) };
}

/** The credits that the packs must still pay before the campaign can start; 0 when none are missing. */
export function reelsPlanMissingCredits(preview: ReelsPlanPreview | null | undefined): number {
  const missing = preview?.missingCredits;
  return typeof missing === 'number' && missing > 0 ? missing : 0;
}

/** True for a length that the server takes: a whole number from 3 to 60. */
export function isReelsPlanLength(days: number): boolean {
  return Number.isInteger(days) && days >= REELS_PLAN_MIN_DAYS && days <= REELS_PLAN_MAX_DAYS;
}

/** True for a start day that is a day and is not before today. */
export function isReelsPlanStart(startDay: string, today: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(startDay) && startDay >= today.slice(0, 10);
}

/** The usable channels that take a reel (a video), in the given order. */
export function reelsPlanPlatforms(usable: SocialPlatform[]): SocialPlatform[] {
  return usable.filter((platform) => platformTakesMedia(platform, 'video'));
}

export interface ReelsPlanStripDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** The day of the month, as the strip shows it. */
  dayOfMonth: number;
  /** The day gets a reel. */
  on: boolean;
}

/** The day strip of the setup (frame f1): every day of the length; a filled day gets its reels. */
export function reelsPlanStrip(settings: ReelsPlanSettings): ReelsPlanStripDay[] {
  if (!isReelsPlanLength(settings.days)) {
    return [];
  }
  const on = new Set(reelsPlanOffsets(settings));
  return Array.from({ length: settings.days }, (_unused, offset) => {
    const date = reelsPlanDateOf(settings.startDay, offset);
    return { date, dayOfMonth: Number(date.slice(8, 10)), on: on.has(offset) };
  });
}

export interface ReelsPlanSummary {
  reels: number;
  credits: number;
  posts: number;
  /**
   * The channels that get each reel: the posts of the dry run divided by the reels (QA finding
   * B14). It comes from the server, so it counts only the channels that really take the posts.
   */
  channels: number;
  /** The first and the last day with a reel (`YYYY-MM-DD`); null with no day. */
  from: string | null;
  to: string | null;
  /** The credits that the workspace can spend; null for a plan with no limit. */
  creditsLeft: number | null;
}

/** The numbers of the summary line (frame f1). Null while there is no answer, or for a broken one. */
export function reelsPlanSummary(preview: ReelsPlanPreview | null | undefined): ReelsPlanSummary | null {
  if (preview === null || preview === undefined || typeof preview.reelCount !== 'number') {
    return null;
  }
  const days = Array.isArray(preview.days) ? preview.days : [];
  return {
    reels: preview.reelCount,
    credits: preview.credits,
    posts: preview.postCount,
    channels: preview.reelCount > 0 ? Math.round(preview.postCount / preview.reelCount) : 0,
    from: days[0]?.date ?? null,
    to: days[days.length - 1]?.date ?? null,
    creditsLeft: preview.creditsLeft ?? null,
  };
}

/** `checking`: no answer yet. `ready`: the plan can start. Else the reason that stops it (frame f2). */
export type ReelsPlanFootKind = 'checking' | 'ready' | ReelsPlanRefusal;

export function reelsPlanFootKind(preview: ReelsPlanPreview | null | undefined): ReelsPlanFootKind {
  if (reelsPlanSummary(preview) === null || preview === null || preview === undefined) {
    return 'checking';
  }
  if (preview.refusal !== null && preview.refusal !== undefined) {
    return preview.refusal;
  }
  return preview.canMake ? 'ready' : 'unavailable';
}

const REFUSAL_MESSAGES: Record<ReelsPlanRefusal, MessageDescriptor> = {
  postsPerDayNeedsPlan: reelsPlanMessages.refusalPostsPerDayNeedsPlan,
  planExists: reelsPlanMessages.refusalPlanExists,
  needsPlan: reelsPlanMessages.refusalNeedsPlan,
  noMaster: reelsPlanMessages.refusalNoMaster,
  masterProcessing: reelsPlanMessages.refusalMasterProcessing,
  notEnoughCredits: reelsPlanMessages.noteShort,
  noChannels: reelsPlanMessages.refusalNoChannels,
  unavailable: reelsPlanMessages.refusalUnavailable,
};

/** The sentence of a refusal of the dry run (frame f2). An unknown refusal reads as "not available". */
export function reelsPlanRefusalMessage(refusal: ReelsPlanRefusal): MessageDescriptor {
  return REFUSAL_MESSAGES[refusal] ?? reelsPlanMessages.refusalUnavailable;
}

/**
 * The amber note "A long plan can repeat parts of the song" (frame f2): more reels than twice the
 * strong parts of the song. A song with no analysis yet has no count to name, so it gets no note.
 */
export function reelsPlanShowsRepeatNote(preview: ReelsPlanPreview | null | undefined): boolean {
  if (reelsPlanFootKind(preview) !== 'ready' || preview === null || preview === undefined) {
    return false;
  }
  return preview.hookCandidates > 0 && reelsPlanCanRepeat(preview.reelCount, preview.hookCandidates);
}

/** A step of an AI reels plan with no ready reel: its reel failed, is made now, or waits in the queue. */
export type ReelsStepWait = 'failed' | 'making' | 'queued';

/**
 * What a step of an AI reels plan waits for (frame f3), or null for a step with a ready reel.
 * `failed` ("The reel did not finish. 1 credit came back.", "Make it again") is only a step whose
 * asset is `missing`: the reel failed and gave its credit back. A step whose reel is made now
 * (`working`) is `making` or `queued`, also when it has the drafts of a reel before it: the artist
 * rejected or replaced that reel, and the step moved to the new one.
 */
export function reelsStepWait(step: PlanStepView): ReelsStepWait | null {
  if (step.assetState === 'working') {
    return (step.day.asset?.making ?? 0) > 0 ? 'making' : 'queued';
  }
  if (step.assetState === 'missing' && step.channels.every((channel) => channel.state === 'notPlanned')) {
    return 'failed';
  }
  return null;
}

/** "3 of 7 reels ready" and the bar of the run sheet head (frame f3). */
export function reelsPlanProgress(steps: PlanStepView[]): { ready: number; count: number; percent: number } {
  const ready = steps.filter((step) => step.assetState === 'ready').length;
  const count = steps.length;
  return { ready, count, percent: count === 0 ? 0 : Math.round((ready / count) * 100) };
}

/**
 * The counts of the "End the plan" confirm (frame f4): the drafts and the failed posts are deleted,
 * the scheduled and "needs you" posts are canceled. A post that went out, or goes out now, stays.
 */
export function reelsPlanEndCounts(steps: PlanStepView[]): { drafts: number; scheduled: number } {
  const states = steps.flatMap((step) => step.channels.map((channel) => channel.state));
  return {
    drafts: states.filter((state) => state === 'draft' || state === 'failed').length,
    scheduled: states.filter((state) => state === 'scheduled' || state === 'needsYou').length,
  };
}

/**
 * The steps that the run sheet shows (frame f3): `limit` steps from the first step that is not all
 * posted, and the others folded. `showAll` shows every step.
 */
export function foldReelsSteps(
  steps: PlanStepView[],
  showAll: boolean,
  limit = REELS_PLAN_VISIBLE_STEPS
): { visible: PlanStepView[]; hidden: PlanStepView[] } {
  if (showAll || steps.length <= limit) {
    return { visible: steps, hidden: [] };
  }
  const firstOpen = steps.findIndex((step) => step.tone !== 'ok');
  const start = Math.min(firstOpen === -1 ? 0 : firstOpen, steps.length - limit);
  return {
    visible: steps.slice(start, start + limit),
    hidden: [...steps.slice(0, start), ...steps.slice(start + limit)],
  };
}

/**
 * The shortfalls of the dry run (design limit-upsell a1, a6): the video credits of the reels
 * against the credits left (plan plus packs), and the posts of the campaign against the posts left
 * this month. A plan with no limit, or no answer, has none. Pure.
 */
export function reelsPlanShortfalls(preview: ReelsPlanPreview | null | undefined): {
  credits: LimitShortfall | null;
  posts: LimitShortfall | null;
} {
  if (preview === null || preview === undefined) {
    return { credits: null, posts: null };
  }
  return {
    credits:
      typeof preview.creditsLeft === 'number'
        ? limitShortfall('videoCredits', preview.credits, preview.creditsLeft)
        : null,
    posts:
      typeof preview.scheduledPostsLimit === 'number'
        ? limitShortfall('posts', preview.postCount, preview.scheduledPostsLeft ?? 0)
        : null,
  };
}
